import React from "react";
import { Switch } from "components/Graphs/Switch";
import {
  RadioCardGroup,
  RadioCardItem,
  RadioCardIndicator,
} from "components/Graphs/RadioCardGroup";
import {
  LockClosedIcon,
  LinkIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { AccessLevel } from "./LaunchableAccessControl";

export interface LaunchableViewAccessSelectorProps {
  /** Current access level value */
  value: AccessLevel;
  /** Callback when access level changes */
  onChange: (value: AccessLevel) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional heading content. Pass null when the parent provides the heading. */
  label?: React.ReactNode;
}

/**
 * LaunchableViewAccessSelector Component
 *
 * A component for selecting launchable view access levels with a toggle for
 * published/searchable status and radio buttons for visibility options.
 *
 * When published (toggle ON), shows a fixed "Everyone" option with globe icon.
 * When not published (toggle OFF), shows radio buttons for "Anyone with the link"
 * and "Only my organization".
 */
export const LaunchableViewAccessSelector: React.FC<
  LaunchableViewAccessSelectorProps
> = ({ value, onChange, disabled = false, label = "View Access" }) => {
  return (
    <div>
      {label && (
        <label className="text-sm font-medium text-primary mb-2.5 block">
          {label}
        </label>
      )}

      {/* Toggle for Published/Searchable */}
      <div className="mb-3 flex items-center">
        <Switch
          checked={value === "published"}
          onCheckedChange={(checked) => {
            if (checked) {
              onChange("published");
            } else {
              // Default to public when unpublished
              onChange("public");
            }
          }}
          disabled={disabled}
        />
        <label className="ml-3 text-sm font-medium text-primary">
          Publish to community
        </label>
      </div>

      {/* When Published: Show Everyone with Globe Icon */}
      {value === "published" ? (
        <div className="flex items-center px-3 py-2.5 border border-borderPrimary rounded-md bg-secondaryBg">
          <GlobeAltIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" />
          <div>
            <div className="text-sm font-medium text-white">Everyone</div>
            <div className="text-xs text-secondary">
              Publicly discoverable by anyone
            </div>
          </div>
        </div>
      ) : (
        /* When Not Published: Show Radio Options */
        <RadioCardGroup
          value={value}
          onValueChange={(val) => onChange(val as AccessLevel)}
        >
          <div className="space-y-2">
            <RadioCardItem
              value="public"
              className="flex items-center space-x-3 p-3"
              disabled={disabled}
            >
              <RadioCardIndicator />
              <LinkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-white">
                  Anyone with the link
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Anyone with the link can view and deploy
                </div>
              </div>
            </RadioCardItem>

            <RadioCardItem
              value="organization"
              className="flex items-center space-x-3 p-3"
              disabled={disabled}
            >
              <RadioCardIndicator />
              <LockClosedIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <div className="flex-1 text-sm font-medium text-white">
                Only my organization
              </div>
            </RadioCardItem>
          </div>
        </RadioCardGroup>
      )}
    </div>
  );
};
